import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const BUCKET = "fukt-foton";

export interface DampScanRow {
  id: string;
  created_at: string;
  vehicle: string | null;
  model_year: string | null;
  area: string | null;
  note: string | null;
  meter_value: string | null;
  risk_level: number;
  risk_label: string | null;
  estimated_moisture_pct: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assessment: any;
  photo_paths: string[];
  share_token: string;
  is_shared: boolean;
}

const SaveSchema = z.object({
  vehicle: z.string().trim().max(160).optional().or(z.literal("")),
  modelYear: z.string().trim().max(20).optional().or(z.literal("")),
  area: z.string().trim().max(200).optional().or(z.literal("")),
  note: z.string().trim().max(1200).optional().or(z.literal("")),
  meterValue: z.string().trim().max(60).optional().or(z.literal("")),
  riskLevel: z.coerce.number().int().min(0).max(4),
  riskLabel: z.string().trim().max(120).optional().or(z.literal("")),
  estimatedMoisturePct: z.string().trim().max(60).optional().or(z.literal("")),
  assessment: z.unknown(),
  photoPaths: z.array(z.string().max(400)).max(4).default([]),
});

/** Sparar en fuktskanning på den inloggade användaren. */
export const saveDampScan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SaveSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await (context.supabase as any)
      .from("damp_scans")
      .insert({
        user_id: context.userId,
        vehicle: data.vehicle || null,
        model_year: data.modelYear || null,
        area: data.area || null,
        note: data.note || null,
        meter_value: data.meterValue || null,
        risk_level: data.riskLevel,
        risk_label: data.riskLabel || null,
        estimated_moisture_pct: data.estimatedMoisturePct || null,
        assessment: data.assessment ?? {},
        photo_paths: data.photoPaths,
      })
      .select("id,share_token,is_shared,created_at")
      .single();
    if (error) throw new Error(error.message);
    return row as { id: string; share_token: string; is_shared: boolean; created_at: string };
  });

/** Användarens egna skanningar, nyast först. */
export const listMyDampScans = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("damp_scans")
      .select(
        "id,created_at,vehicle,model_year,area,note,meter_value,risk_level,risk_label,estimated_moisture_pct,assessment,photo_paths,share_token,is_shared",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    return { scans: (data ?? []) as DampScanRow[] };
  });

/** Slår på eller av den delbara länken för en egen skanning. */
export const setDampScanShared = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid(), shared: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { data: row, error } = await (context.supabase as any)
      .from("damp_scans")
      .update({ is_shared: data.shared })
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .select("id,is_shared,share_token")
      .single();
    if (error) throw new Error(error.message);
    return row as { id: string; is_shared: boolean; share_token: string };
  });

/** Tar bort en egen skanning och dess bilder. */
export const deleteDampScan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: row } = await (context.supabase as any)
      .from("damp_scans")
      .select("photo_paths")
      .eq("id", data.id)
      .eq("user_id", context.userId)
      .maybeSingle();
    const { error } = await (context.supabase as any)
      .from("damp_scans")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    const paths = Array.isArray(row?.photo_paths) ? (row.photo_paths as string[]) : [];
    if (paths.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await (supabaseAdmin as any).storage.from(BUCKET).remove(paths);
    }
    return { ok: true };
  });

/**
 * Publik rapport via hemlig länk. Läses bara om ägaren aktivt delat den.
 * Bilderna exponeras som tidsbegränsade länkar, aldrig som öppen mapp.
 */
export const getSharedDampScan = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().trim().min(16).max(80) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const admin = supabaseAdmin as any;
    const { data: row, error } = await admin
      .from("damp_scans")
      .select(
        "id,created_at,vehicle,model_year,area,note,meter_value,risk_level,risk_label,estimated_moisture_pct,assessment,photo_paths,is_shared",
      )
      .eq("share_token", data.token)
      .eq("is_shared", true)
      .maybeSingle();
    if (error || !row) return { scan: null as null, photoUrls: [] as string[] };

    const paths = Array.isArray(row.photo_paths) ? (row.photo_paths as string[]) : [];
    let photoUrls: string[] = [];
    if (paths.length > 0) {
      const { data: signed } = await admin.storage
        .from(BUCKET)
        .createSignedUrls(paths, 60 * 60 * 24 * 7);
      photoUrls = ((signed ?? []) as Array<{ signedUrl?: string }>)
        .map((s) => s.signedUrl ?? "")
        .filter(Boolean);
    }

    const { photo_paths: _omit, ...safe } = row as Record<string, unknown>;
    return { scan: safe as Omit<DampScanRow, "photo_paths" | "share_token">, photoUrls };
  });
